import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import axios from 'axios';
import { userLoggedIn } from '../../redux/actions';
import styles from './Header.module.scss';
import operaLogo from '../scss/hf-images/opera-sync.png';
import User from './User/User';
import RequiresFeature, { Features, isFeatureSupported } from '../Page/Utils/RequiresFeature';
import { purgeLocalData } from '../../proto/utils/updates';
import logoutIcon from '../scss/hf-images/logout.svg';

function LogoutButton() {
  const { t } = useTranslation();

  const logoutText = t('Logout');
  return (
    <>
      <a
        href={new URL('/ui_backend/oauth2/logout/', process.env.REACT_APP_PROXY_HOST)}
        rel="noopener noreferrer"
      >
        <img src={logoutIcon} className={styles['logout-icon']} alt={logoutText} />
      </a>
      <div className={styles.tooltipText}>{logoutText}</div>
    </>
  );
}

function LoginButton() {
  const { t } = useTranslation();
  return (
    <a
      href={new URL('/ui_backend/oauth2/login/', process.env.REACT_APP_PROXY_HOST)}
      rel="noopener noreferrer"
      className={styles['login-link']}
    >
      {t('Login')}
    </a>
  );
}

function Header({ isUpdating, dispatchUserLoggedIn }) {
  const [userdata, setUserdata] = useState();
  const { t } = useTranslation();

  useEffect(() => {
    if (isFeatureSupported(Features.DISPLAY_GRID)) {
      (async () => {
        let response;
        try {
          response = await axios({
            method: 'get',
            url: new URL('/ui_backend/proxy/auth/api/v1/me', process.env.REACT_APP_PROXY_HOST),
            withCredentials: true,
          });
        } catch (e) {
          if (e.response && e.response.status === 403) {
            setUserdata(null);
            await purgeLocalData();
            window.open(new URL('/ui_backend/oauth2/login/', process.env.REACT_APP_PROXY_HOST), '_self');
            return;
          }
          throw e;
        }
        setUserdata(response.data);
        if (localStorage.getItem('user_id') !== response.data.id) {
          await purgeLocalData(true);
          localStorage.setItem('user_id', response.data.id);
        }
        if (response.data) {
          dispatchUserLoggedIn();
        }
      })();
    }
  }, [dispatchUserLoggedIn]);

  const faqLanguage = {
    fr: 'fr',
    de: 'de',
    pl: 'pl',
    'pt-br': 'pt-br',
    pt: 'pt-br',
    ru: 'ru',
    'es-es': 'es',
    'es-419': 'es',
  }[localStorage.getItem('i18nextLng')] || 'en';

  return (
    <header className={`${styles.hf} ${styles.hf__header}`}>
      <div className={styles['hf--primary']}>
        <div className={styles['hf-wrapper']}>
          <div className={styles['hf--width-holder']}>
            <div
              className={styles['hf--main-nav']}
              role="navigation"
            >
              <a
                className={styles['hf--brand']}
                href={process.env.REACT_APP_BASE_PATH}
              >
                <img src={operaLogo} alt="Opera" />
              </a>
              <div className={styles['hf--menu']}>
                <nav
                  className={styles['hf--menu-nav']}
                  id={styles['hf--m-menu']}
                >
                  <ul
                    className={styles['hf--main-nav-items']}
                    role="menu"
                  >
                    <li className={`${styles['hf--main-nav-item']} ${styles['hf--main-nav-item__expandable']}`}>
                      <span className={styles['hf--main-nav-item-tile']}>
                        <a href={`https://help.opera.com/${faqLanguage}/account/`}>
                          {t('Opera sync FAQ')}
                        </a>
                      </span>
                    </li>
                  </ul>
                </nav>
              </div>
              <RequiresFeature features={[Features.DISPLAY_GRID]}>
                <div className={styles['hf--menu-download']}>
                  <span className={styles['download-button--wrapper']}>
                    <div className={styles.button}>
                      { userdata ? (
                        <>
                          {
                            isUpdating
                              ? <div className={styles.spinner} />
                              : null
                          }
                          <User userdata={userdata} />
                          <LogoutButton />
                        </>
                      ) : (
                        <LoginButton />
                      )}
                    </div>
                  </span>
                </div>
              </RequiresFeature>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

Header.propTypes = {
  isUpdating: PropTypes.bool.isRequired,
  dispatchUserLoggedIn: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  isUpdating: state.isUpdating,
});

const mapDispatchToProps = (dispatch) => ({
  dispatchUserLoggedIn: () => dispatch(userLoggedIn()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(Header);
